import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useStores } from '@/components/StoreProvider'
import Layout from '@/components/Layout/Layout'
import LoadingSpinner from '@/components/Common/LoadingSpinner'
import { useNavigate } from 'react-router'
import { History, Search } from 'lucide-react'
import Show from '@/components/Common/Show'
import { Input } from '@/components/ui/input'

const HistoryPage: React.FC = observer(() => {
  const { chatHistoryStore, userStore } = useStores()
  const navigate = useNavigate()

  useEffect(() => {
    // Fetch initial user sessions when component mounts if sessions are empty
    if (chatHistoryStore.sessions.length === 0) {
      chatHistoryStore.fetchInitialUserSessions()
    }
  }, [chatHistoryStore])

  // Debounced search effect - only for admin users
  useEffect(() => {
    if (!userStore.isAdmin) return

    const timer = setTimeout(() => {
      if (chatHistoryStore.searchValue.length > 2 || chatHistoryStore.searchValue.length === 0) {
        chatHistoryStore.setEmailFilter(chatHistoryStore.searchValue)
        chatHistoryStore.fetchInitialUserSessions()
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [chatHistoryStore.searchValue, chatHistoryStore, userStore.isAdmin])

  const handleChatClick = (chatId: string, isInProgress: boolean) => {
    // Prevent opening chat if session is still in progress
    if (isInProgress) {
      return
    }
    // Navigate to chat detail page when a chat is clicked
    navigate(`/chat/${chatId}`)
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-136px)] bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="container mx-auto p-4 max-w-3xl">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-0 p-6 hover:shadow-2xl transition-all duration-300">
            <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-primary to-primary/70 rounded-xl shadow-md">
                  <History className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Chat History</h2>
              </div>
            </div>

            {/* Search Input - Only visible for admin users */}
            {userStore.isAdmin && (
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search by user email (min 3 characters)..."
                    value={chatHistoryStore.searchValue}
                    onChange={(e) => chatHistoryStore.setSearchValue(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>
                {chatHistoryStore.searchValue.length > 0 && chatHistoryStore.searchValue.length <= 2 && <p className="text-sm text-gray-500 mt-2">Enter at least 3 characters to search</p>}
              </div>
            )}

            {chatHistoryStore.error && (
              <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg mb-4 shadow-md">{chatHistoryStore.error}</div>
            )}

            {chatHistoryStore.sessions.length === 0 && !chatHistoryStore.isLoading && (
              <div className="text-center py-16">
                <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
                  <History className="h-12 w-12 text-gray-400" />
                </div>
                <p className="text-lg text-gray-500 font-medium">No chat sessions found.</p>
                <p className="text-sm text-gray-400 mt-2">Your conversation history will appear here</p>
              </div>
            )}

            <div className="space-y-3">
              {chatHistoryStore.sessions.map((session) => {
                const coachSession =
                  session.messages.find(
                    (fold) =>
                      fold.fold_name === 'coach_fold' ||
                      fold.fold_name === 'Coach' ||
                      fold.fold_name === 'Coach Voice Collector' ||
                      fold.fold_name === 'Role Play Eng' ||
                      fold.fold_name === 'Feedback Assistant',
                  ) || session.messages.find((fold) => fold.messages?.some((msg) => msg.role === 'assistant' || msg.role === 'user'))
                const chatMessages = coachSession?.messages.filter((msg) => msg.content).filter((msg) => msg.role === 'assistant' || msg.role === 'user') || []
                const sessionDuration = session.startTime && session.endTime ? new Date(session.endTime).getTime() - new Date(session.startTime).getTime() : null

                const isInProgress = !session.endTime

                return (
                  <div
                    key={session.sessionToken}
                    onClick={() => handleChatClick(session.sessionToken, isInProgress)}
                    className={`border-0 rounded-xl p-5 transition-all duration-300 flex justify-between items-center group shadow-md ${
                      isInProgress
                        ? 'opacity-60 cursor-not-allowed bg-gradient-to-r from-gray-50 to-gray-100'
                        : 'hover:shadow-xl cursor-pointer bg-gradient-to-r from-white to-gray-50 hover:scale-[1.01] hover:translate-x-1'
                    }`}
                  >
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-800 group-hover:text-primary transition-colors">Session #{session.id}</h3>
                      <div className="text-sm text-gray-500 flex flex-wrap gap-4 mt-2">
                        <span className="inline-flex items-center gap-1 font-medium">
                          <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                          {chatHistoryStore.formatDate(session.createdAt)}
                        </span>
                        <span className="inline-flex items-center gap-1 font-medium">
                          <span className="w-1.5 h-1.5 bg-accent rounded-full"></span>
                          {session.appName}
                        </span>
                        <Show condition={!!chatMessages}>
                          <span className="inline-flex items-center gap-1 font-medium">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                            {chatMessages.length || 0} messages
                          </span>
                        </Show>
                        {sessionDuration && (
                          <span className="inline-flex items-center gap-1 font-medium">
                            <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                            Duration: {Math.round(sessionDuration / 1000 / 60)} min
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      {session.endTime ? (
                        <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-50 to-green-100 text-green-700 font-semibold rounded-full text-sm shadow-sm">Completed</span>
                      ) : (
                        <div className="text-center">
                          <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700 font-semibold rounded-full text-sm shadow-sm">
                            In Progress
                          </span>
                          <div className="text-xs text-gray-500 mt-2">Cannot view active sessions</div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {chatHistoryStore.isLoading && (
              <div className="flex justify-center py-6">
                <LoadingSpinner size="lg" text="Loading history..." color="teal" />
              </div>
            )}

            {chatHistoryStore.hasMore && !chatHistoryStore.isLoading && (
              <div className="pt-6 flex justify-center">
                <button
                  onClick={() => chatHistoryStore.fetchMoreUserSessions()}
                  className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  View More
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
})

export default HistoryPage
